import { apiClient } from '../../api/client'
import APIs from '../../api/index'

export const getUserList = (params) => apiClient.get(APIs.GUARDIAN_USERS.list, { params })

export const getUserByUuid = (uuid) => apiClient.get(APIs.GUARDIAN_USERS.byUuid(uuid))

// CSV export — request a blob so axios doesn't try to parse it as JSON. The
// backend contract is `GET /api/guardian-users/export` with the same query
// params as the list endpoint (`limit`, `page`, `search`, `direction`,
// `sortBy`, `filterFields`, `filterInputs`) plus a repeated `columns` query
// param selecting the columns to include in the CSV.
//
// `selectedUuids` lets the caller export only the rows the user has ticked
// via the table checkboxes; we send them as `filterFields=uuid` with one
// `filterInputs=<uuid>` per selected row, matching the list endpoint's
// filter contract.
export const exportUsers = (params = {}, { columns, selectedUuids } = {}) => {
  const config = { params, responseType: 'blob' }
  const needsArraySerializer =
    (Array.isArray(columns) && columns.length > 0) ||
    (Array.isArray(selectedUuids) && selectedUuids.length > 0)
  if (needsArraySerializer) {
    // axios serializes arrays as repeated keys (`columns=a&columns=b` /
    // `filterInputs=u1&filterInputs=u2`) which matches the curl example in
    // the backend contract.
    config.paramsSerializer = { indexes: null }
  }
  if (Array.isArray(columns) && columns.length > 0) {
    config.params.columns = columns
  }
  if (Array.isArray(selectedUuids) && selectedUuids.length > 0) {
    config.params.filterFields = ['uuid', ...(params.filterFields || [])]
    config.params.filterInputs = [...selectedUuids, ...(params.filterInputs || [])]
  }
  return apiClient.get(APIs.GUARDIAN_USERS.export, config)
}

export const createUser = (payload) => apiClient.post(APIs.GUARDIAN_USERS.create, payload)

export const updateUser = (uuid, payload) =>
  apiClient.put(APIs.GUARDIAN_USERS.update(uuid), payload)

// PUT body contract: { status, role } where `role` is the role's UUID.
// `roleUuid` is optional so callers that only want to flip status can omit
// it; the backend treats absent fields as "leave unchanged" for PATCH-style
// semantics on this endpoint.
export const updateUserStatus = (uuid, status, roleUuid) => {
  const body = { status }
  if (roleUuid) body.role = roleUuid
  return apiClient.put(APIs.GUARDIAN_USERS.update(uuid), body)
}

export const deleteUser = (uuid) => apiClient.delete(APIs.GUARDIAN_USERS.delete(uuid))

export const bulkDeleteUsers = async (uuids) => {
  await Promise.all(uuids.map((uuid) => apiClient.delete(APIs.GUARDIAN_USERS.delete(uuid))))
}

export const getRoleList = (params = {}) => apiClient.get(APIs.ROLE_PERMISSION.getList, { params })

export const getBranchList = (params = {}) => apiClient.get(APIs.BRANCHES.list, { params })

// API responses are wrapped: { status, data: { data: [...], total } } for
// paginated lists, { status, data: <user> } for most single-record endpoints,
// and { status, data: [{...}] } (a one-element array) for `GET /guardian-users/:uuid`.
// These helpers accept either an axios response or the already-unwrapped body.
export function extractList(response) {
  const payload = response?.data ?? response
  if (Array.isArray(payload?.data?.data)) return payload.data.data
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload)) return payload
  return []
}

export function extractTotal(response) {
  const payload = response?.data ?? response
  // Backend paginated shape: { status, data: { data: [...], meta: { total, current_page, per_page, ... } } }
  if (typeof payload?.data?.meta?.total === 'number') return payload.data.meta.total
  if (typeof payload?.data?.total === 'number') return payload.data.total
  if (typeof payload?.total === 'number') return payload.total
  if (typeof payload?.meta?.total === 'number') return payload.meta.total
  return 0
}

export function extractItem(response) {
  const payload = response?.data ?? response
  // GET-by-uuid returns the user inside a one-element array.
  if (Array.isArray(payload?.data) && payload.data.length > 0 && !Array.isArray(payload.data[0])) {
    return payload.data[0]
  }
  // GET-by-uuid (alt shape) wraps the array one level deeper: data: { data: [...] }.
  if (
    Array.isArray(payload?.data?.data) &&
    payload.data.data.length > 0 &&
    !Array.isArray(payload.data.data[0])
  ) {
    return payload.data.data[0]
  }
  if (payload?.data?.data && !Array.isArray(payload.data.data)) return payload.data.data
  if (payload?.data && !Array.isArray(payload.data)) return payload.data
  return null
}

export function normalizeUser(u) {
  if (!u) return null
  const firstRelation = Array.isArray(u.GuardianUserRoleRelations)
    ? u.GuardianUserRoleRelations[0]
    : null
  const statusFromArray =
    Array.isArray(u.Status) && u.Status.length > 0 ? u.Status[0]?.status : null
  const rawStatus = u.status ?? statusFromArray ?? ''
  const platformMapping = Array.isArray(u.PlatformMappings) ? u.PlatformMappings[0] : null
  const branchMapping = Array.isArray(u.GuardianBranchMappings) ? u.GuardianBranchMappings[0] : null
  const address = u.Address || {}
  const addressValue =
    address.addressLine ||
    [
      address.addressLine1,
      address.addressLine2,
      address.city,
      address.state,
      address.country,
      address.zip,
    ]
      .filter(Boolean)
      .join(', ')
  const platformId = platformMapping?.platformId ?? null
  const branchId = branchMapping?.branchId ?? null
  const platformName = platformMapping?.platformName ?? ''
  const branchName = branchMapping?.branchName ?? ''
  const roleName = firstRelation?.GuardianRole?.name ?? firstRelation?.name ?? ''
  const roleUuid = firstRelation?.GuardianRole?.uuid ?? firstRelation?.uuid ?? ''
  return {
    uuid: u.uuid ?? u.id,
    // Backend exposes a single `name` field; keep it as `name` for all
    // consumers that display the user's identity.
    name: u.name ?? '',
    address: addressValue,
    email: u.email ?? '',
    phoneNumber: u.phoneNo ?? '',
    status: rawStatus || 'INACTIVE',
    role: roleName,
    roleUuid,
    roleId: firstRelation?.roleId ?? null,
    branchId,
    branchName,
    branch: '',
    platformId,
    platformName,
    companyId: u.companyId ?? null,
    createdAt: u.createdAt ?? null,
    raw: u,
  }
}
